import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { toArabicDigits, formatRelativeTime } from '../../../utils/numbers';

const mapCommentFromApi = (c) => ({
  id: c.id,
  userId: c.userId,
  author: c.userName,
  authorImage: c.userAvatar,
  authorImageAlt: c.userName || 'صورة المستخدم',
  content: c.content,
  timestamp: formatRelativeTime(c.createdAt),
  likeCount: c.likeCount ?? 0,
  hasUserLiked: c.hasUserLiked ?? false,
  replies: (c.replies || []).map(mapCommentFromApi)
});

const CommentItem = ({
  comment,
  depth = 0,
  onReply,
  onDelete,
  onEdit,
  onLike,
  currentUserId,
  isAuthenticated,
  isAdmin
}) => {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showMenu, setShowMenu] = useState(false);

  const maxDepth = 3;
  const isOwner = currentUserId && String(comment.userId) === String(currentUserId);
  const canReply = depth < maxDepth && isAuthenticated;
  const canDelete = isAuthenticated && (isOwner || isAdmin);
  const canEdit = isAuthenticated && (isOwner || isAdmin);

  const handleReplySubmit = async () => {
    if (!replyText?.trim() || !onReply) return;
    setSubmitting(true);
    try {
      await onReply(comment.id, replyText);
      setReplyText('');
      setShowReplyBox(false);
    } catch {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = () => {
    setEditingId(comment.id);
    setEditText(comment.content || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleEditSubmit = async () => {
    if (!editText?.trim() || !onEdit) return;
    setSubmitting(true);
    try {
      await onEdit(comment.id, editText.trim());
      setEditingId(null);
      setEditText('');
    } catch {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {depth > 0 && (
        <div
          className="absolute top-0 right-0 w-[2px] h-full"
          style={{ backgroundColor: 'rgba(197, 160, 89, 0.2)' }}
        />
      )}

      <div
        className="mb-6"
        style={{
          marginRight: depth > 0 ? `${depth * 40}px` : '0',
          paddingRight: depth > 0 ? '20px' : '0'
        }}
      >
        <div className="rounded-2xl p-6 transition-all duration-300">
          <div className="flex items-start gap-4 mb-4">
            <Image
              src={comment?.authorImage}
              alt={comment?.authorImageAlt || comment?.author}
              className="w-12 h-12 rounded-full object-cover shadow-warm"
            />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h4
                  className="text-base font-bold"
                  style={{ fontFamily: 'Amiri, serif', color: '#2C2B29' }}
                >
                  {comment?.author}
                </h4>
                <div className="flex items-center gap-2">
                  <span
                    className="text-sm"
                    style={{ fontFamily: 'Amiri, serif', color: '#999' }}
                  >
                    {comment?.timestamp}
                  </span>
                  {(canEdit || canDelete) && editingId !== comment.id && (
                    <div className="relative flex-shrink-0 overflow-visible">
                      <button
                        type="button"
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-1.5 hover:bg-[rgba(197,160,89,0.1)] rounded-lg transition-colors"
                        aria-label="خيارات التعليق"
                      >
                        <Icon name="MoreVertical" size={18} color="#999" />
                      </button>
                      {showMenu && (
                        <div className="absolute left-0 top-full mt-2 bg-white rounded-lg py-1.5 min-w-[120px] z-50 shadow-warm border border-[rgba(197,160,89,0.15)]">
                          {canEdit && onEdit && (
                            <button
                              type="button"
                              onClick={() => {
                                setShowMenu(false);
                                editingId === comment.id ? cancelEdit() : startEdit();
                              }}
                              className="w-full px-4 py-2 text-right text-sm hover:bg-[rgba(197,160,89,0.08)] transition-colors flex items-center gap-2"
                              style={{ fontFamily: 'Amiri, serif', color: '#2C2B29' }}
                            >
                              <Icon name="Edit" size={16} color="#5A5A5A" />
                              <span>تعديل</span>
                            </button>
                          )}
                          {canDelete && onDelete && (
                            <button
                              type="button"
                              onClick={() => {
                                setShowMenu(false);
                                if (window.confirm('هل أنت متأكد من حذف هذا التعليق؟')) {
                                  onDelete(comment.id);
                                }
                              }}
                              className="w-full px-4 py-2 text-right text-sm hover:bg-[rgba(220,38,38,0.08)] transition-colors flex items-center gap-2 text-red-600"
                              style={{ fontFamily: 'Amiri, serif' }}
                            >
                              <Icon name="Trash2" size={16} color="currentColor" />
                              <span>حذف</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {editingId === comment.id ? (
            <div className="mb-4">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="عدّل تعليقك..."
                className="w-full p-3 rounded-lg bg-[#F9F7F2] border border-[rgba(197,160,89,0.2)] focus:outline-none focus:border-[#C5A059] transition-all duration-300 resize-none"
                style={{
                  fontFamily: 'Amiri, serif',
                  fontSize: '14px',
                  minHeight: '80px',
                  direction: 'rtl'
                }}
                autoFocus
              />
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelEdit}
                  disabled={submitting}
                  style={{ fontFamily: 'Amiri, serif' }}
                >
                  إلغاء
                </Button>
                <Button
                  size="sm"
                  onClick={handleEditSubmit}
                  disabled={!editText?.trim() || submitting}
                  style={{
                    backgroundColor: '#C5A059',
                    color: 'white',
                    fontFamily: 'Amiri, serif'
                  }}
                >
                  {submitting ? 'جاري الحفظ...' : 'حفظ'}
                </Button>
              </div>
            </div>
          ) : (
            <p
              className="text-base leading-relaxed mb-4"
              style={{
                fontFamily: 'Amiri, serif',
                color: '#2C2B29',
                lineHeight: '1.8'
              }}
            >
              {comment?.content}
            </p>
          )}

          <div className="flex items-center gap-4">
            {isAuthenticated && onLike && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onLike(comment.id, comment.hasUserLiked);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-300 ${
                  comment.hasUserLiked ? 'bg-[rgba(197,160,89,0.15)]' : 'hover:bg-[rgba(197,160,89,0.1)]'
                }`}
              >
                <Icon
                  name={comment.hasUserLiked ? 'Heart' : 'Heart'}
                  size={16}
                  color={comment.hasUserLiked ? '#C5A059' : '#999'}
                  fill={comment.hasUserLiked ? '#C5A059' : 'none'}
                />
                <span
                  className="text-sm"
                  style={{ fontFamily: 'Amiri, serif', color: comment.hasUserLiked ? '#C5A059' : '#999' }}
                >
                  إعجاب ({toArabicDigits(comment.likeCount ?? 0)})
                </span>
              </button>
            )}
            {!isAuthenticated && (
              <span className="flex items-center gap-2 px-3 py-1.5 text-sm" style={{ fontFamily: 'Amiri, serif', color: '#999' }}>
                <Icon name="Heart" size={16} color="#999" />
                {toArabicDigits(comment.likeCount ?? 0)}
              </span>
            )}
            {canReply && (
              <button
                onClick={() => setShowReplyBox(!showReplyBox)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-300 hover:bg-[rgba(197,160,89,0.1)]"
              >
                <Icon name="MessageCircle" size={16} color="#999" />
                <span
                  className="text-sm"
                  style={{ fontFamily: 'Amiri, serif', color: '#999' }}
                >
                  رد
                </span>
              </button>
            )}
          </div>

          {showReplyBox && (
            <div className="mt-4 pt-4 border-t border-[rgba(197,160,89,0.1)]">
              <div className="flex gap-3">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e?.target?.value)}
                  placeholder="اكتب ردك هنا..."
                  className="flex-1 p-3 rounded-lg bg-[#F9F7F2] border border-[rgba(197,160,89,0.2)] focus:outline-none focus:border-[#C5A059] transition-all duration-300 resize-none"
                  style={{
                    fontFamily: 'Amiri, serif',
                    fontSize: '14px',
                    minHeight: '80px',
                    direction: 'rtl'
                  }}
                />
              </div>
              <div className="flex items-center justify-end gap-2 mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowReplyBox(false)}
                  style={{ fontFamily: 'Amiri, serif' }}
                >
                  إلغاء
                </Button>
                <Button
                  size="sm"
                  onClick={handleReplySubmit}
                  disabled={!replyText?.trim() || submitting}
                  style={{
                    backgroundColor: '#C5A059',
                    color: 'white',
                    fontFamily: 'Amiri, serif'
                  }}
                >
                  {submitting ? 'جاري النشر...' : 'نشر الرد'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {comment?.replies && comment?.replies?.length > 0 && (
          <div className="mt-4">
            {comment?.replies?.map((reply) => (
              <CommentItem
                key={reply?.id}
                comment={reply}
                depth={depth + 1}
                onReply={onReply}
                onDelete={onDelete}
                onEdit={onEdit}
                onLike={onLike}
                currentUserId={currentUserId}
                isAuthenticated={isAuthenticated}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CommentThread = ({
  comments = [],
  loading,
  onCommentSubmit,
  onReplySubmit,
  onDeleteComment,
  onEditComment,
  onLikeComment,
  isAuthenticated,
  currentUserId,
  isAdmin
}) => {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mappedComments = comments.map(mapCommentFromApi);

  const handleCommentSubmit = async () => {
    if (!newComment?.trim() || !onCommentSubmit) return;
    setSubmitting(true);
    try {
      await onCommentSubmit(newComment);
      setNewComment('');
    } catch {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12" style={{ fontFamily: 'Amiri, serif', color: '#5A5A5A' }}>
        جاري تحميل التعليقات...
      </div>
    );
  }

  return (
    <div>
      {isAuthenticated && (
        <div className="mb-12">
          <h3
            className="text-2xl mb-6"
            style={{
              fontFamily: 'Aref Ruqaa, serif',
              color: '#2C2B29',
              fontWeight: 700
            }}
          >
            أضف تعليقك
          </h3>

          <div className="rounded-2xl p-6">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e?.target?.value)}
              placeholder="شارك أفكارك حول هذا المقال..."
              className="w-full p-4 rounded-lg bg-[#F9F7F2] border border-[rgba(197,160,89,0.2)] focus:outline-none focus:border-[#C5A059] transition-all duration-300 resize-none"
              style={{
                fontFamily: 'Amiri, serif',
                fontSize: '16px',
                minHeight: '120px',
                direction: 'rtl',
                lineHeight: '1.8'
              }}
            />

            <div className="flex items-center justify-between mt-4">
              <span
                className="text-sm"
                style={{ fontFamily: 'Amiri, serif', color: '#999' }}
              >
                {toArabicDigits(newComment?.length ?? 0)} حرف
              </span>

              <Button
                onClick={handleCommentSubmit}
                disabled={!newComment?.trim() || submitting}
                style={{
                  backgroundColor: '#C5A059',
                  color: 'white',
                  fontFamily: 'Amiri, serif',
                  fontSize: '16px',
                  fontWeight: 600
                }}
              >
                {submitting ? 'جاري النشر...' : 'نشر التعليق'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!isAuthenticated && (
        <p
          className="mb-8 text-center"
          style={{ fontFamily: 'Amiri, serif', color: '#5A5A5A' }}
        >
          سجّل الدخول لإضافة تعليق
        </p>
      )}

      <div>
        <h3
          className="text-2xl mb-6"
          style={{
            fontFamily: 'Aref Ruqaa, serif',
            color: '#2C2B29',
            fontWeight: 700
          }}
        >
          التعليقات ({toArabicDigits(mappedComments?.length ?? 0)})
        </h3>

        {mappedComments?.length === 0 ? (
          <p
            className="text-center py-8"
            style={{ fontFamily: 'Amiri, serif', color: '#999' }}
          >
            لا توجد تعليقات بعد. كن أول من يعلق!
          </p>
        ) : (
          mappedComments?.map((comment) => (
            <CommentItem
              key={comment?.id}
              comment={comment}
              depth={0}
              onReply={onReplySubmit}
              onDelete={onDeleteComment}
              onEdit={onEditComment}
              onLike={onLikeComment}
              currentUserId={currentUserId}
              isAuthenticated={isAuthenticated}
              isAdmin={isAdmin}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default CommentThread;
